import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Switch,
} from "@mui/material";
import {
  inviteHrFirebaseUser,
  listHrFirebaseUsers,
  removeHrFirebaseUserFromCompany,
  updateHrFirebaseUserHideFromHrUi,
} from "../../services/api.service";
import "./invite-hr-user.component.scss";

const InviteHrUserComponent = () => {
  const { company_id } = useParams<{ company_id: string }>();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [hideFromHrUi, setHideFromHrUi] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [users, setUsers] = useState<
    Array<{
      uid: string;
      email: string;
      role?: string;
      display_name?: string | null;
      invited_at?: string | null;
      hide_from_hr_ui?: boolean;
    }>
  >([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [removeTarget, setRemoveTarget] = useState<{ uid: string; email: string } | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [hideToggleUid, setHideToggleUid] = useState<string | null>(null);

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbar({ open: true, message, severity });
  };

  const loadUsers = useCallback(async () => {
    if (!company_id) return;
    setListLoading(true);
    try {
      const res = await listHrFirebaseUsers(company_id);
      if (res.data?.is_success && Array.isArray(res.data.data)) {
        setUsers(res.data.data);
      } else {
        setUsers([]);
      }
    } catch {
      setUsers([]);
      showSnackbar("โหลดรายชื่อไม่สำเร็จ", "error");
    } finally {
      setListLoading(false);
    }
  }, [company_id]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company_id) return;
    const em = email.trim().toLowerCase();
    if (!em) {
      showSnackbar("กรุณากรอกอีเมล", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await inviteHrFirebaseUser(company_id, {
        email: em,
        display_name: displayName.trim() || undefined,
        role,
        hide_from_hr_ui: hideFromHrUi,
      });
      if (res.data?.is_success) {
        const hint =
          res.data.data?.existing_firebase_account === true
            ? " (บัญชีเดิม — อีเมลแจ้งให้เข้าสู่ระบบ ไม่มีลิงก์ตั้งรหัสผ่าน)"
            : res.data.data?.existing_firebase_account === false
              ? " (บัญชีใหม่ — อีเมลมีลิงก์ตั้งรหัสผ่าน)"
              : "";
        showSnackbar((res.data.message || "ส่งคำเชิญแล้ว") + hint, "success");
        setEmail("");
        setDisplayName("");
        setHideFromHrUi(false);
        loadUsers();
      } else {
        showSnackbar(res.data?.error || "เชิญไม่สำเร็จ", "error");
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      showSnackbar(msg || "เชิญไม่สำเร็จ", "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmRemoveFromCompany = async () => {
    if (!company_id || !removeTarget) return;
    setRemoveLoading(true);
    try {
      const res = await removeHrFirebaseUserFromCompany(company_id, removeTarget.uid);
      if (res.data?.is_success) {
        showSnackbar(res.data.message || "นำออกจากองค์กรแล้ว", "success");
        setRemoveTarget(null);
        loadUsers();
      } else {
        showSnackbar(res.data?.error || "นำออกไม่สำเร็จ", "error");
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      showSnackbar(msg || "นำออกไม่สำเร็จ", "error");
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleHideFromHrUiToggle = async (uid: string, nextHidden: boolean) => {
    if (!company_id) return;
    setHideToggleUid(uid);
    setUsers((prev) =>
      prev.map((u) => (u.uid === uid ? { ...u, hide_from_hr_ui: nextHidden } : u)),
    );
    try {
      const res = await updateHrFirebaseUserHideFromHrUi(company_id, uid, nextHidden);
      if (res.data?.is_success) {
        showSnackbar(res.data.message || (nextHidden ? "ซ่อนจาก HR Landing แล้ว" : "แสดงใน HR Landing แล้ว"), "success");
      } else {
        setUsers((prev) =>
          prev.map((u) => (u.uid === uid ? { ...u, hide_from_hr_ui: !nextHidden } : u)),
        );
        showSnackbar(res.data?.error || "อัปเดตไม่สำเร็จ", "error");
      }
    } catch (err: unknown) {
      setUsers((prev) =>
        prev.map((u) => (u.uid === uid ? { ...u, hide_from_hr_ui: !nextHidden } : u)),
      );
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      showSnackbar(msg || "อัปเดตไม่สำเร็จ", "error");
    } finally {
      setHideToggleUid(null);
    }
  };

  return (
    <div className="invite-hr-user-page">
      <Typography variant="h5" className="invite-hr-title">
        เชิญผู้ใช้ HR (อีเมล / รหัสผ่าน)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        ระบบจะตรวจอัตโนมัติ: ถ้าอีเมลนี้ยังไม่มีบัญชี Firebase จะส่งอีเมลพร้อมลิงก์ตั้งรหัสผ่าน — ถ้ามีบัญชีอยู่แล้วจะส่งอีเมลแจ้งว่าถูกเพิ่มเข้าองค์กรและให้เข้าสู่ระบบด้วยรหัสผ่านเดิม (ไม่ส่งลิงก์ตั้งรหัสใหม่)
        หลังล็อกอิน หากผู้ใช้อยู่หลายองค์กรจะต้องเลือกองค์กรก่อน หากเหลือองค์กรเดียวจะเข้าหน้ารายการคำถามได้ทันที
      </Typography>

      <Paper elevation={0} className="invite-hr-form-paper">
        <form onSubmit={handleInvite}>
          <Box display="flex" flexDirection="column" gap={2} maxWidth={480}>
            <TextField
              required
              label="อีเมลผู้รับเชิญ"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="ชื่อแสดง (ไม่บังคับ)"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
              size="small"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>บทบาท</InputLabel>
              <Select
                value={role}
                label="บทบาท"
                onChange={(e) => setRole(e.target.value as "member" | "admin")}
              >
                <MenuItem value="member">สมาชิก</MenuItem>
                <MenuItem value="admin">ผู้ดูแล (HR admin)</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideFromHrUi}
                  onChange={(e) => setHideFromHrUi(e.target.checked)}
                />
              }
              label="ซ่อนอีเมลนี้จาก UI ใน HR Landing (ไม่แสดงในรายชื่อ / autocomplete)"
            />
            <Box>
              <Button type="submit" variant="contained" disabled={loading} sx={{ minWidth: 140 }}>
                {loading ? <CircularProgress size={22} color="inherit" /> : "ส่งคำเชิญ"}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        ผู้ใช้ที่เชิญแล้ว
      </Typography>
      <TableContainer component={Paper} elevation={0} className="invite-hr-table-wrap">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>อีเมล</TableCell>
              <TableCell>บทบาท</TableCell>
              <TableCell>ชื่อแสดง</TableCell>
              <TableCell>ซ่อนใน HR UI</TableCell>
              <TableCell>เชิญเมื่อ</TableCell>
              <TableCell align="right" width={140}>
                จัดการ
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "text.secondary" }}>
                  ยังไม่มีรายการ
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.uid}>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.role || "member"}</TableCell>
                  <TableCell>{u.display_name || "—"}</TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={u.hide_from_hr_ui === true}
                      disabled={hideToggleUid === u.uid || removeLoading}
                      onChange={(_, checked) => handleHideFromHrUiToggle(u.uid, checked)}
                      inputProps={{ "aria-label": `ซ่อน ${u.email} จาก HR Landing` }}
                    />
                  </TableCell>
                  <TableCell>
                    {u.invited_at
                      ? new Date(u.invited_at).toLocaleString("th-TH", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={removeLoading}
                      onClick={() => setRemoveTarget({ uid: u.uid, email: u.email || u.uid })}
                    >
                      นำออกจากองค์กร
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!removeTarget} onClose={() => !removeLoading && setRemoveTarget(null)}>
        <DialogTitle>นำออกจากองค์กร</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            ยืนยันนำ <strong>{removeTarget?.email}</strong> ออกจากองค์กรนี้หรือไม่ ผู้ใช้จะไม่เห็นองค์กรนี้หลังล็อกอิน
            (บัญชี Firebase ยังอยู่ — หากเป็นสมาชิกองค์กรอื่นยังใช้ได้)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)} disabled={removeLoading}>
            ยกเลิก
          </Button>
          <Button color="error" variant="contained" onClick={confirmRemoveFromCompany} disabled={removeLoading}>
            {removeLoading ? <CircularProgress size={22} color="inherit" /> : "นำออก"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default InviteHrUserComponent;
